import { useState, useEffect } from 'react';
import { Search, Bell, TrendingUp, Database, Link2 } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { ContentRecord, Connector, AIReadinessStat } from '../../types/content';
import { MetricCard, Badge, EmptyState } from '../shared';

export function ContentDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [aiReadyCount, setAiReadyCount] = useState(0);
  const [activeConnectors, setActiveConnectors] = useState<Connector[]>([]);
  const [syncsToday, setSyncsToday] = useState(0);
  const [readinessByType, setReadinessByType] = useState<AIReadinessStat[]>([]);
  const [recentRecords, setRecentRecords] = useState<ContentRecord[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;

    try {
      // Load connectors
      const connectorsQuery = query(
        collection(db, 'connectors'),
        where('userId', '==', user.uid),
        where('status', '==', 'active')
      );
      const connectorsSnap = await getDocs(connectorsQuery);
      const connectors = connectorsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as Connector);
      setActiveConnectors(connectors);

      // Load content records
      const recordsQuery = query(
        collection(db, 'contentRecords'),
        where('userId', '==', user.uid)
      );
      const recordsSnap = await getDocs(recordsQuery);
      const records = recordsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as ContentRecord);

      setTotalRecords(records.length);
      setAiReadyCount(records.filter(r => r.aiReadiness === 'excellent' || r.aiReadiness === 'good').length);

      // Calculate readiness by type
      const typeStats: Record<string, AIReadinessStat> = {};
      records.forEach(record => {
        if (!typeStats[record.contentType]) {
          typeStats[record.contentType] = {
            contentType: record.contentType,
            total: 0,
            excellent: 0,
            good: 0,
            needsWork: 0,
            poor: 0,
            averageScore: 0
          };
        }
        const stat = typeStats[record.contentType];
        stat.total++;
        stat[record.aiReadiness]++;
        stat.averageScore += record.aiReadinessScore;
      });

      const statsArray = Object.values(typeStats).map(stat => ({
        ...stat,
        averageScore: Math.round(stat.averageScore / stat.total)
      }));
      setReadinessByType(statsArray);

      // Load recent records
      const recentQuery = query(
        collection(db, 'contentRecords'),
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc'),
        limit(10)
      );
      const recentSnap = await getDocs(recentQuery);
      setRecentRecords(recentSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as ContentRecord));

      // Load syncs today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const syncsQuery = query(
        collection(db, 'syncEvents'),
        where('userId', '==', user.uid),
        where('startedAt', '>=', today)
      );
      const syncsSnap = await getDocs(syncsQuery);
      setSyncsToday(syncsSnap.size);

    } catch (error) {
      console.error('Error loading content dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t2-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Content</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Dashboard</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search content..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t2-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t2-primary uppercase tracking-wide mb-1">Kodiac Content</p>
          <h1 className="text-3xl font-semibold text-gray-900">Content Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Manage and optimize your content for AI platforms</p>
        </div>

        {/* 4-Stat Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <MetricCard
            label="Total Records"
            value={totalRecords.toString()}
          />
          <MetricCard
            label="AI Ready"
            value={aiReadyCount.toString()}
          />
          <MetricCard
            label="Active Connectors"
            value={activeConnectors.length.toString()}
          />
          <MetricCard
            label="Syncs Today"
            value={syncsToday.toString()}
          />
        </div>

        {/* Active Connectors Grid */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Active Connectors</h2>
          {activeConnectors.length === 0 ? (
            <EmptyState
              icon={Link2}
              title="No active connectors"
              description="Connect your content sources to start syncing"
            />
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {activeConnectors.map(connector => (
                <div key={connector.id} className="bg-surface-warm rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">{connector.name}</h3>
                    <Badge variant={connector.status === 'active' ? 'success' : 'critical'}>
                      {connector.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>{connector.recordCount} records</p>
                    {connector.lastSync && (
                      <p>Last sync: {connector.lastSync.toDate().toLocaleString()}</p>
                    )}
                    {connector.errorCount > 0 && (
                      <p className="text-critical-red">{connector.errorCount} errors</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* AI Readiness by Type */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">AI Readiness by Content Type</h2>
            {readinessByType.length === 0 ? (
              <EmptyState
                icon={Database}
                title="No content data"
                description="Sync content to see readiness metrics"
              />
            ) : (
              <div className="space-y-4">
                {readinessByType.map(stat => (
                  <div key={stat.contentType}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {stat.contentType}
                      </span>
                      <span className="text-sm font-semibold text-t2-primary">{stat.averageScore}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 flex overflow-hidden">
                      <div
                        className="bg-success-green h-2"
                        style={{ width: `${(stat.excellent / stat.total) * 100}%` }}
                        title={`${stat.excellent} excellent`}
                      />
                      <div
                        className="bg-blue-400 h-2"
                        style={{ width: `${(stat.good / stat.total) * 100}%` }}
                        title={`${stat.good} good`}
                      />
                      <div
                        className="bg-warning-amber h-2"
                        style={{ width: `${(stat.needsWork / stat.total) * 100}%` }}
                        title={`${stat.needsWork} needs work`}
                      />
                      <div
                        className="bg-critical-red h-2"
                        style={{ width: `${(stat.poor / stat.total) * 100}%` }}
                        title={`${stat.poor} poor`}
                      />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-600 mt-1">
                      <span>{stat.total} total</span>
                      <span className="text-success-green">{stat.excellent} excellent</span>
                      <span className="text-blue-400">{stat.good} good</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Records Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Recent Records</h2>
            {recentRecords.length === 0 ? (
              <EmptyState
                icon={TrendingUp}
                title="No records yet"
                description="Connect a source to start syncing content"
              />
            ) : (
              <div className="space-y-2">
                {recentRecords.map(record => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between p-3 bg-surface-warm rounded-lg border border-gray-200"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{record.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="primary">{record.contentType}</Badge>
                        <Badge
                          variant={
                            record.status === 'approved'
                              ? 'success'
                              : record.status === 'suppressed'
                              ? 'critical'
                              : 'warning'
                          }
                        >
                          {record.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-sm font-semibold text-t2-primary">
                        {record.aiReadinessScore}
                      </div>
                      <div className="text-xs text-gray-500">AI Score</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
